import * as THREE from 'three';
import { HighwaySegmentConfig, HIGHWAY_TIERS, getTierColor } from './Highway';

export class HighwaySegment {
  public readonly config: HighwaySegmentConfig;
  public readonly mesh: THREE.Group;

  // Computed properties
  private readonly length: number;
  private readonly direction: THREE.Vector3;
  private readonly center: THREE.Vector3;
  private readonly rotation: number;

  // For height sampling
  private readonly localMatrix: THREE.Matrix4;
  private readonly inverseLocalMatrix: THREE.Matrix4;

  constructor(scene: THREE.Scene, config: HighwaySegmentConfig) {
    this.config = config;
    this.mesh = new THREE.Group();

    // Calculate segment properties
    const diff = config.endPos.clone().sub(config.startPos);
    this.length = diff.length();
    this.direction = diff.normalize();
    this.center = config.startPos.clone().add(config.endPos).multiplyScalar(0.5);
    this.rotation = Math.atan2(this.direction.x, this.direction.z);

    // Create transformation matrices for local/world space conversion
    this.localMatrix = new THREE.Matrix4();
    this.localMatrix.makeRotationY(this.rotation);
    this.localMatrix.setPosition(this.center.x, this.getHeightAt(0.5), this.center.z);

    this.inverseLocalMatrix = this.localMatrix.clone().invert();

    // Build visual mesh
    this.createMesh();
    scene.add(this.mesh);
  }

  private createMesh(): void {
    const tierColor = getTierColor(this.config.tier);

    // Main road surface
    const roadGeom = new THREE.BoxGeometry(this.config.width, 0.5, this.length);
    const roadMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e,
      metalness: 0.3,
      roughness: 0.7,
    });
    const road = new THREE.Mesh(roadGeom, roadMat);
    road.position.y = -0.25;
    this.mesh.add(road);

    // Neon edge strips
    if (this.config.hasNeonStripes) {
      const stripGeom = new THREE.BoxGeometry(0.2, 0.3, this.length);
      const stripMat = new THREE.MeshStandardMaterial({
        color: tierColor,
        emissive: tierColor,
        emissiveIntensity: 1.5,
      });

      const leftStrip = new THREE.Mesh(stripGeom, stripMat);
      leftStrip.position.set(-this.config.width / 2 + 0.1, 0.1, 0);
      this.mesh.add(leftStrip);

      const rightStrip = new THREE.Mesh(stripGeom, stripMat.clone());
      rightStrip.position.set(this.config.width / 2 - 0.1, 0.1, 0);
      this.mesh.add(rightStrip);
    }

    // Guardrails
    if (this.config.hasGuardrails) {
      this.createGuardrails(tierColor);
    }

    // Center line markings
    this.createCenterLine();

    // Support pillars for elevated segments
    if (this.config.tier > 0 || (this.config.startHeight && this.config.startHeight > 0)) {
      this.createSupportPillars();
    }

    // Position and rotate the mesh
    this.mesh.position.copy(this.center);
    this.mesh.position.y = this.getHeightAt(0.5);
    this.mesh.rotation.y = this.rotation;

    // For ramps, also apply pitch
    if (this.config.type === 'ramp' && this.config.startHeight !== undefined && this.config.endHeight !== undefined) {
      const heightDiff = this.config.endHeight - this.config.startHeight;
      const pitch = Math.atan2(heightDiff, this.length);
      this.mesh.rotation.x = -pitch;
    }
  }

  private createGuardrails(tierColor: number): void {
    const railHeight = 1.2;
    const postSpacing = 8;
    const postCount = Math.ceil(this.length / postSpacing);

    // Posts material
    const postMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.8,
    });

    // Neon rail material
    const railMat = new THREE.MeshStandardMaterial({
      color: tierColor,
      emissive: tierColor,
      emissiveIntensity: 1.2,
    });

    for (let side = -1; side <= 1; side += 2) {
      const xOffset = (this.config.width / 2 - 0.3) * side;

      // Posts
      for (let i = 0; i <= postCount; i++) {
        const zOffset = -this.length / 2 + i * (this.length / postCount);
        const postGeom = new THREE.BoxGeometry(0.2, railHeight, 0.2);
        const post = new THREE.Mesh(postGeom, postMat);
        post.position.set(xOffset, railHeight / 2, zOffset);
        this.mesh.add(post);
      }

      // Horizontal rail
      const railGeom = new THREE.BoxGeometry(0.1, 0.15, this.length);
      const rail = new THREE.Mesh(railGeom, railMat);
      rail.position.set(xOffset, railHeight - 0.1, 0);
      this.mesh.add(rail);
    }
  }

  private createCenterLine(): void {
    const dashCount = Math.floor(this.length / 8);
    const dashMat = new THREE.MeshStandardMaterial({
      color: 0xffff00,
      emissive: 0xffff00,
      emissiveIntensity: 0.3,
    });

    for (let i = 0; i < dashCount; i++) {
      const dashGeom = new THREE.BoxGeometry(0.15, 0.05, 3);
      const dash = new THREE.Mesh(dashGeom, dashMat);
      dash.position.set(0, 0.03, -this.length / 2 + (i + 0.5) * (this.length / dashCount));
      this.mesh.add(dash);
    }
  }

  private createSupportPillars(): void {
    const pillarSpacing = 40;
    const pillarCount = Math.max(2, Math.ceil(this.length / pillarSpacing));

    for (let i = 0; i <= pillarCount; i++) {
      const t = i / pillarCount;
      const zOffset = -this.length / 2 + t * this.length;
      const height = this.getHeightAt(t);

      if (height > 1) {
        this.createPillar(0, zOffset, height);
      }
    }
  }

  private createPillar(x: number, z: number, height: number): void {
    const pillarGroup = new THREE.Group();

    // Main pillar (tapered)
    const pillarGeom = new THREE.CylinderGeometry(0.8, 1.2, height, 8);
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.6,
      roughness: 0.3,
    });
    const pillar = new THREE.Mesh(pillarGeom, pillarMat);
    pillar.position.y = -height / 2;
    pillarGroup.add(pillar);

    // Neon ring accent near top
    const ringGeom = new THREE.TorusGeometry(1.0, 0.08, 8, 16);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 1.5,
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -1;
    pillarGroup.add(ring);

    pillarGroup.position.set(x, 0, z);
    this.mesh.add(pillarGroup);
  }

  // Get the height at a normalized position (0 = start, 1 = end)
  private getHeightAt(t: number): number {
    if (this.config.type === 'ramp' && this.config.startHeight !== undefined && this.config.endHeight !== undefined) {
      return this.config.startHeight + t * (this.config.endHeight - this.config.startHeight);
    }
    return HIGHWAY_TIERS[this.config.tier].height;
  }

  // Check if a world position is on this highway segment and return height
  public getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    // Transform world position to local segment space
    const localPos = worldPos.clone().applyMatrix4(this.inverseLocalMatrix);

    // Check if within segment bounds (width and length)
    const halfWidth = this.config.width / 2 + 0.5; // Small margin
    const halfLength = this.length / 2 + 0.5;

    if (Math.abs(localPos.x) > halfWidth || Math.abs(localPos.z) > halfLength) {
      return null;
    }

    // Calculate normalized position along segment (0 to 1)
    const t = (localPos.z + this.length / 2) / this.length;
    const segmentHeight = this.getHeightAt(Math.max(0, Math.min(1, t)));

    // Check vertical proximity (don't snap from too far above/below)
    const maxHeightDiff = 2.5;
    if (Math.abs(worldPos.y - segmentHeight) > maxHeightDiff) {
      return null;
    }

    return segmentHeight;
  }

  // Get bounding box for spatial partitioning
  public getBounds(): { minX: number; maxX: number; minZ: number; maxZ: number } {
    const halfWidth = this.config.width / 2 + 2;
    const points = [this.config.startPos, this.config.endPos];

    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    for (const p of points) {
      minX = Math.min(minX, p.x - halfWidth);
      maxX = Math.max(maxX, p.x + halfWidth);
      minZ = Math.min(minZ, p.z - halfWidth);
      maxZ = Math.max(maxZ, p.z + halfWidth);
    }

    return { minX, maxX, minZ, maxZ };
  }

  public destroy(scene: THREE.Scene): void {
    scene.remove(this.mesh);
  }
}
